import { Edit, LoaderCircle, Trash2 } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableCellWithTooltip,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { useGetModels } from '@/hooks/useGetModels';
import { FilterModelsDto, Model } from '@/services/ModelsService';

export function ModelsTable({
  filter,
  onEdit,
  onRemove,
}: {
  filter: FilterModelsDto;
  onEdit: (model: Model) => void;
  onRemove: (model: Model) => void;
}) {
  const endRef = useRef<HTMLDivElement>(null);

  const { models, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useGetModels({
      serviceName: filter.serviceName,
      promptName: filter.promptName,
    });

  useEffect(() => {
    if (!endRef.current) return;
    const observer = new IntersectionObserver((entries, obs) => {
      const { isIntersecting } = entries[0];
      if (!hasNextPage) {
        obs.disconnect();
        return;
      }
      if (isIntersecting && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    observer.observe(endRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Service</TableHead>
            <TableHead>Model Version</TableHead>
            <TableHead>Prompt</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Updated At</TableHead>
            <TableHead className="w-24">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {models.length === 0 && !isFetching && (
            <TableRow>
              <TableCell
                colSpan={6}
                className="py-10 text-center text-gray-500"
              >
                No models found.
              </TableCell>
            </TableRow>
          )}
          {models.map((m, index) => (
            <TableRow key={`${m.id}-${index}`} className="hover:bg-gray-100">
              <TableCellWithTooltip className="font-medium">
                {m.service}
              </TableCellWithTooltip>
              <TableCellWithTooltip className="font-medium">
                {m.modelVersion}
              </TableCellWithTooltip>
              <TableCellWithTooltip className="max-w-lg truncate">
                {m.promptName ?? m.promptId}
              </TableCellWithTooltip>
              <TableCell>{m.customerId}</TableCell>
              <TableCellWithTooltip className="text-sm text-gray-600">
                {m.updatedAt
                  ? new Date(m.updatedAt).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })
                  : '-'}
              </TableCellWithTooltip>
              <TableCell className="text-center">
                <div className="flex items-center justify-start gap-2">
                  <Button
                    onClick={() => onEdit(m)}
                    variant="ghost"
                    size="sm"
                    className="hover:bg-white"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => onRemove(m)}
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-white hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div ref={endRef} />
      {(isFetching || isFetchingNextPage) && (
        <div className="flex justify-center py-4">
          <LoaderCircle className="h-6 w-6 animate-spin" />
        </div>
      )}
    </div>
  );
}
